import React from 'react';
import { graphql } from 'gatsby';
import parse from 'html-react-parser';
import Footer from '@ecs/tng-common/components/footer/Footer';
import StickyBannerDynamic from '../../components/StickyBannerDynamic';
import SEO from '../../components/SEO';
import BottomPromoBanner from '../../components/posts/BottomPromoBanner';
import LayoutArticles from '../../components/LayoutArticles';
import FooterDisclosures from '../../components/ FooterDisclosures';
import { contWidthClases, getCustomFieldValue, setSeoMetaTags } from '../../helpers/functions';
import { addAlternateHreflang, setDataSection, setEcsSiteSection } from '../../helpers/analytics';
import {
  IAuthor,
  IPost,
  ExpCustomFields,
  QSWidgetTypes,
  CalculatorTypes
} from '../../helpers/interfaces';
import PostContent from '../../components/posts/PostContent';
import TableOfContents, { formTOCLinks } from '../../components/posts/TableOfContents';
import TopAdvertiserDisclosure from '../../components/posts/TopAdvertiserDisclosure';
import BlogCategoryLinks from '../../components/BlogCategoryLinks';
import { calcMarker } from '../../helpers/calculators';
import {
  checkIfToIncludeBoostDisclosure,
  checkIfToIncludeVantageDisclosure
} from '../../helpers/single-post-replacements';
import { calculatorExploreMoreLinks } from '../../content/blog-category-links';
import { useInfogramScript, useQSScript } from '../../helpers/embed-script';
import SocialShare from '../../components/SocialShare';
import { pathPrefix } from '../../helpers/routes';
// styles
import '../../styles/post-default-template.scss';

interface ICalcTemplateProps {
  data: {
    post: IPost;
    author?: IAuthor; // optional, doesn't come through in preview query
  };
  // eslint-disable-next-line react/no-unused-prop-types
  pageContext?: {
    // optional, doesn't come through in preview query
    postId: string;
    authId: string;
  };
}
export default function CalculatorsTemplate({ data }: ICalcTemplateProps) {
  const postData = data.post;
  const expCustomFields = postData?.expCustomFields || [];
  const { categories, title, databaseId, slug, content } = postData;
  const { databaseId: categoryId, slug: catSlug } = categories?.nodes[0] ?? {};

  const postContent = content || '';

  const ifInfogramExists = postContent.includes('infogram-embed');
  const parts = postContent.split(calcMarker);
  // Combine the first part with the divider
  const firstPart = parts[0] + calcMarker;
  const secondPart = parts[1] || '';

  const calculator = getCustomFieldValue(
    ExpCustomFields.Calculator,
    expCustomFields
  ) as CalculatorTypes | null;
  const ctaQuinn: QSWidgetTypes = getCustomFieldValue(
    ExpCustomFields.CtaQuinn,
    expCustomFields
  ) as QSWidgetTypes;
  const ctaCustom = getCustomFieldValue(ExpCustomFields.CTACustom, expCustomFields);
  const TableOfContentsConent = formTOCLinks(postContent);
  const calcExploreMoreLinks = calculatorExploreMoreLinks.filter(
    (linkObj) => linkObj?.link?.replace(/\//g, '') !== slug
  );

  // Load embed scripts
  useInfogramScript(ifInfogramExists);
  useQSScript(ctaQuinn);

  return (
    <LayoutArticles>
      <main id="main">
        <TopAdvertiserDisclosure />
        <article className="full-calc">
          <div className="container container-sm-py">
            <div className="row justify-content-center">
              <div className="col-xxl-10">
                <h1 className="content-mb">{parse(title)}</h1>
                <PostContent
                  content={firstPart}
                  params={{
                    postId: databaseId,
                    categoryId: categoryId,
                    catSlug: catSlug,
                    isSEMtemplate: false,
                    isFullCalculator: true,
                    calculator: calculator,
                    qsWidget: ctaQuinn
                  }}
                />
              </div>
            </div>
          </div>
          <hr className="my-0" />
          <div className="container container-py">
            <div className="row justify-content-xxl-center">
              <div className="col-12 col-xxl-10">
                <div
                  className={`row ${Object.keys(TableOfContentsConent).length > 0 ? 'd-lg-block clearfix gx-lg-5 gx-xl-6 gx-xxl-7' : 'justify-content-center'} `}>
                  <TableOfContents
                    content={TableOfContentsConent}
                    className="float-lg-end"
                  />
                  <div className={contWidthClases}>
                    <PostContent
                      content={secondPart}
                      params={{
                        postId: databaseId,
                        categoryId: categoryId,
                        catSlug: catSlug,
                        isSEMtemplate: false,
                        isFullCalculator: true,
                        calculator: calculator,
                        qsWidget: ctaQuinn
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="col-12 col-xxl-10 mt-4 mt-lg-5">
                <div
                  className={`row ${Object.keys(TableOfContentsConent).length > 0 ? 'd-lg-block clearfix gx-lg-5 gx-xl-6 gx-xxl-7' : 'justify-content-center'} `}>
                  <div className={contWidthClases}>
                    <BottomPromoBanner
                      category={catSlug || ''}
                      ctaCustom={ctaCustom || ''}
                      categoryId={categoryId || ''}
                    />
                    <section>
                      {calcExploreMoreLinks?.length > 0 ? (
                        <BlogCategoryLinks links={calcExploreMoreLinks} />
                      ) : null}
                    </section>
                    <section>
                      <h2 className="eyebrow-text">Share article</h2>
                      <SocialShare
                        title={title}
                        link={`${pathPrefix}${postData?.uri}`} // This used canonical but no longer set by SEO
                      />
                    </section>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </article>

        <StickyBannerDynamic
          category={catSlug || ''}
          ctaCustom={ctaCustom || ''}
          categoryId={categoryId || ''}
        />
      </main>
      <Footer
        pageType="blog"
        disclosures={
          <FooterDisclosures
            boostDisclosure={checkIfToIncludeBoostDisclosure(postContent)}
            vantageScoreDisclosure={
              checkIfToIncludeVantageDisclosure(postContent) || calculator === 'reverse-car'
            }
          />
        }
      />
    </LayoutArticles>
  );
}

export function Head({ data, pageContext }: ICalcTemplateProps) {
  const { categories, expCustomFields, expSeo, uri } = data.post;
  const catSlug = categories?.nodes[0]?.slug || '';
  const catId = categories?.nodes[0]?.databaseId || '';
  const title = expSeo?.title || '';
  const description = expSeo?.description || '';
  const schema = expSeo?.schema || '';
  const schemaScript = schema && <script type="application/ld+json">{schema}</script>;
  const section = setDataSection(catSlug);
  const category = catSlug;
  const authName = data.author?.slug || '';
  const hrefLang = getCustomFieldValue(ExpCustomFields.HrefLanf, expCustomFields || []);
  const quinn = getCustomFieldValue(ExpCustomFields.CtaQuinn, expCustomFields || []) || '';
  const savings = getCustomFieldValue(ExpCustomFields.CtaSavings, expCustomFields || []) || '';
  return (
    <>
      <body
        data-op="EXP-ASK-CLC"
        data-section={section}
        data-category={category}
        data-categoryid={catId.toString()}
        data-id={pageContext?.postId.toString()}
        data-ecscategory={setEcsSiteSection('single', {
          authName,
          section,
          category,
          quinn,
          savings
        })}
      />
      <SEO
        title={title}
        description={description}
        noindex={expSeo?.metaRobotsNoindex !== 'index'}>
        {expSeo && setSeoMetaTags(expSeo)}
        {expSeo?.og?.updated_time && (
          <meta
            property="og:updated_time"
            content={expSeo?.og?.updated_time}
          />
        )}
        {schemaScript}
        {uri && hrefLang ? parse(addAlternateHreflang(hrefLang, uri)) : null}
      </SEO>
    </>
  );
}

// replace   fragment PostAuthor  as it gave me an arror for some reason, need to replace back
export const query = graphql`
  query SinglePost($postId: Int!, $authId: Int!) {
    post: wpPost(databaseId: { eq: $postId }) {
      ...PostContent
      expSeo {
        ...ExpSEOFields
      }
    }
    author: wpUser(databaseId: { eq: $authId }) {
      ...PostAuthor
      expSeo {
        description
      }
    }
  }
`;
